
import OktaSignIn from "@bend-financial/okta-signin-widget";
import oktaConfig from "@/okta-config";

export class OktaSignInWidgetBuilder {
  private widget: OktaSignIn;
  private signInResponse: { data: any };
  private config: {};

  constructor (el) {
    this.widget = null;
    /** default configurations */
    this.config = {
      el,
      baseUrl: oktaConfig.oidc.issuer.split("/oauth2")[0],
      clientId: oktaConfig.oidc.clientId,
      redirectUri: oktaConfig.oidc.redirectUri,
      i18n: {
        en: {
          "primaryauth.title": "For your security, please reauthenticate to manage your authentication settings."
        }
      },
      authParams: {
        issuer: oktaConfig.oidc.issuer,
        scopes: oktaConfig.oidc.scopes
      },
      features: {
        rememberMe: false,
        showPasswordToggleOnSignInPage: true,
      },
      cookies: {
        secure: true
      },
      customButtons: []
    };
  }

  setConfig (config: any): this {
    this.widget = new OktaSignIn(config);
    return this;
  }

  setUsername (username: string): this {
    this.config = { ...this.config, username };
    return this;
  }

  setBrandColor (color: any): this {
    this.config = { ...this.config, colors: { brand: color } };
    return this;
  }

  onCustomCancelButtonClicked (callback) {
    this.config = {
      ... this.config, customButtons: [{
        title: "Cancel",
        className: "customButtonCancel",
        click: () => {
          this.removeSignInWidget();
          callback();
        }
      }]
    };
    return this;
  }

  build (): this {
    this.widget = new OktaSignIn(this.config);
    return this;
  }

  buildForOktaFactorSettings (): this {
    this.widget = new OktaSignIn(this.config);
    this.widget.on("afterRender", context => this.afterRenderEvents(context));
    return this;
  }

  buildForBiometricAuthSetup (): this {
    this.widget = new OktaSignIn(this.config);
    this.widget.on("afterRender", context => this.afterRendEventsForBiometricAuthSetup(context));
    return this;
  }

  afterRenderEvents (context) {
    this.hideUsername(context);
    this.setupCustomCancelButton(context);
    this.setupMfaFactorSelectionView(context);
  }

  afterRendEventsForBiometricAuthSetup (context) {
    this.afterRenderEvents(context);
    this.storePasswordOnSigninSubmit(context);
  }

  showSignIn (): Promise<any> {
    return this.widget
    .showSignIn()
    .then(() => {
      return Promise.resolve(this.signInResponse);
    })
    .finally(() =>
      this.removeSignInWidget()
    );
  }

  setupMfaFactorSelectionView (context) {
    if (context.controller === "mfa-verify") {
      // display mfa factor selection
      this.removeDOMElementAttributeByClass("okta-sign-in-header auth-header", "style");
    }
  }

  storePasswordOnSigninSubmit (context) {
    if (context && context.controller === "primary-auth") {
      const submitButton = document.getElementById("okta-signin-submit");
      const passwordElement: HTMLInputElement = document.getElementById("okta-signin-password") as any;
      submitButton.addEventListener("click", _ => {
        this.signInResponse = { data: passwordElement.value };
      });
    }
  }

  hideUsername (context) {
    if (context && context.controller === "primary-auth") {
      // headerElement (this is a parent of username element)
      this.hideDOMElementByClass("okta-sign-in-header auth-header");
      // usernameElement
      this.hideDOMElementByClass("o-form-fieldset o-form-label-top margin-btm-5");
    }
  }

  setupCustomCancelButton (context) {
    if (context && context.controller === "primary-auth") {
      // custom button divider
      this.hideDOMElementByClass("auth-divider");
      // custom button
      this.removeDOMElementAttributeByClass("customButtonCancel", "class");
      // footerElement
      this.hideDOMElementByClass("auth-footer");
    }
  }

  hideDOMElementByClass (className: string) {
    const element = document.getElementsByClassName(className)[0];
    if (element) {
      element.setAttribute("style", "display:none;");
    }
  }

  removeDOMElementAttributeByClass (className: string, attribute: string) {
    const element = document.getElementsByClassName(className)[0];
    if (element) {
      element.removeAttribute(attribute);
    }
  }

  removeSignInWidget () {
    if (this.widget) {
      this.widget.off();
      this.widget.remove();
    }
  }

  destroyed () {
    this.removeSignInWidget();
  }
}